import { IGrain } from 'src/models';
import { filterMeasurementRowsByRejectSelection } from '../../electron-main/reject-measurement-filter';
import { localizeResultLabel, normalizeResultLabel } from './resultLocalization';

export const PARTICLE_DISTRIBUTION_REJECT_SETTING = 'particle_distribution_reject_config';
export const REJECTED_GRAINS_REJECT_SETTING = 'rejected_grains_reject_config';
export const REJECT_SELECTION_SESSION_PREFIX = 'frontend_reject_selection_config';
export const REJECT_SELECTION_SESSION_DRAFT_PROFILE_ID = '__draft__';

export interface IRejectOption {
  key: string;
  label: string;
  aliases: string[];
  isForeignMatter?: boolean;
  isBroken?: boolean;
}

export interface IRejectSelectionConfig {
  particleDistributionRejectKeys: string[];
  rejectedGrainsRejectKeys: string[];
}

export interface IRejectSelectionSnapshot {
  selectedKeys: string[];
  selectedLabels?: string[];
}

export interface IRejectSelectionSessionConfig {
  particleDistribution: IRejectSelectionSnapshot;
  rejectedGrains: IRejectSelectionSnapshot;
}

const normalizeToken = (value: unknown): string => {
  if (value === null || value === undefined) return '';
  return String(value)
    .trim()
    .replace(/[^a-z0-9]+/gi, '')
    .toLowerCase();
};

const unique = (values: string[]): string[] => Array.from(new Set(values.filter(Boolean)));

const getMapping = (grain: IGrain | undefined): Record<string, string> => {
  return grain?.rejectionMapping || grain?.rawJson?.rejectionMapping || {};
};

const getBrokenParts = (grain: IGrain | undefined): Record<string, { DisplayName?: string; Value?: string }> => {
  return grain?.rawJson?.brokenParts || {};
};

const isForeignMatterAlias = (value: string): boolean => {
  const token = normalizeToken(value);
  return token === 'fm1'
    || token === 'fm2'
    || token === 'foreignmatterstone'
    || token === 'foreignmaterialstone'
    || token === 'foreignmatterother'
    || token === 'foreignmaterialother';
};

const isForeignMatterStoneAlias = (value: string): boolean => {
  const token = normalizeToken(value);
  return token === 'fm1'
    || token === 'foreignmatterstone'
    || token === 'foreignmaterialstone';
};

const isForeignMatterOtherAlias = (value: string): boolean => {
  const token = normalizeToken(value);
  return token === 'fm2'
    || token === 'foreignmatterother'
    || token === 'foreignmaterialother';
};

const buildAliases = (key: string, label: string, extraValues: Array<string | undefined> = []): string[] => {
  return unique([
    normalizeToken(key),
    normalizeToken(label),
    ...tokenizeRejectValue(key),
    ...tokenizeRejectValue(label),
    ...extraValues.map((value) => normalizeToken(value)),
    ...extraValues.flatMap((value) => tokenizeRejectValue(value)),
  ]);
};

const tokenizeRejectValue = (value: unknown): string[] => {
  const normalized = normalizeToken(value);
  const resultLabelKey = normalizeResultLabel(value);
  return unique([
    normalized,
    resultLabelKey ? normalizeToken(resultLabelKey) : '',
  ]);
};

const naturalKeySort = (a: string, b: string) => {
  const aMatch = a.match(/\d+/);
  const bMatch = b.match(/\d+/);
  if (aMatch && bMatch) {
    const aNum = Number(aMatch[0]);
    const bNum = Number(bMatch[0]);
    if (aNum !== bNum) return aNum - bNum;
  }
  return a.localeCompare(b);
};

export function getRejectSelectionSessionKey(grainId?: string | number, profileId?: string | number): string {
  return `${REJECT_SELECTION_SESSION_PREFIX}:${grainId ?? ''}:${profileId ?? ''}`;
}

export function getRejectOptions(
  grain: IGrain | undefined,
  optionsConfig: { localizeLabels?: boolean } = {}
): IRejectOption[] {
  const shouldLocalizeLabels = optionsConfig.localizeLabels ?? true;
  const mapping = getMapping(grain);
  const options: IRejectOption[] = Object.entries(mapping).map(([key, label]) => ({
    key,
    label: shouldLocalizeLabels ? (localizeResultLabel(label) || label || key) : (label || key),
    aliases: buildAliases(key, label, [
      label,
      key,
      isForeignMatterAlias(key) || isForeignMatterAlias(label)
        ? label.replace(/foreign material/i, 'Foreign Matter')
        : undefined,
      isForeignMatterAlias(key) || isForeignMatterAlias(label)
        ? label.replace(/foreign matter/i, 'Foreign Material')
        : undefined,
      isForeignMatterStoneAlias(label) ? 'FM1' : undefined,
      isForeignMatterOtherAlias(label) ? 'FM2' : undefined,
    ]),
    isForeignMatter: isForeignMatterAlias(key) || isForeignMatterAlias(label),
  }));

  const brokenParts = getBrokenParts(grain);
  const brokenPartKeys = Object.keys(brokenParts);
  const brokenAliases = brokenPartKeys.flatMap((key) => {
    const part = brokenParts[key];
    return buildAliases(key, part?.DisplayName || 'Broken', [part?.DisplayName, part?.Value]);
  });
  options.push({
    key: 'Broken',
    label: shouldLocalizeLabels ? (localizeResultLabel('Broken') || 'Broken') : 'Broken',
    aliases: unique([
      normalizeToken('Broken'),
      normalizeToken('isBroken'),
      ...brokenAliases,
    ]),
    isBroken: true,
  });

  return options.sort((a, b) => {
    if (a.isBroken && !b.isBroken) return 1;
    if (!a.isBroken && b.isBroken) return -1;
    return naturalKeySort(a.key, b.key);
  });
}

export function getDefaultRejectSelectionConfig(grain: IGrain | undefined): IRejectSelectionConfig {
  const options = getRejectOptions(grain);
  return {
    particleDistributionRejectKeys: options
      .filter((option) => !option.isForeignMatter)
      .map((option) => option.key),
    rejectedGrainsRejectKeys: options.map((option) => option.key),
  };
}

function extractSelectedKeysFromSnapshot(
  snapshot: unknown,
  options: IRejectOption[]
): string[] {
  if (!snapshot || typeof snapshot !== 'object') {
    return [];
  }

  const value = snapshot as {
    selectedKeys?: unknown;
    selectedLabels?: unknown;
  };

  const rawKeys = Array.isArray(value.selectedKeys) ? value.selectedKeys : [];
  const rawLabels = Array.isArray(value.selectedLabels) ? value.selectedLabels : [];
  return normalizeRejectSelectionKeys([...rawKeys, ...rawLabels], options);
}

function buildAliasLookup(options: IRejectOption[]) {
  const lookup = new Map<string, string>();
  options.forEach((option) => {
    lookup.set(normalizeToken(option.key), option.key);
    lookup.set(normalizeToken(option.label), option.key);
    option.aliases.forEach((alias) => {
      lookup.set(normalizeToken(alias), option.key);
    });
  });
  return lookup;
}

export function normalizeRejectSelectionKeys(
  selectedKeys: unknown,
  options: IRejectOption[]
): string[] {
  if (!options || options.length === 0) {
    return unique((Array.isArray(selectedKeys) ? selectedKeys : []).map((key) => String(key)));
  }
  const lookup = buildAliasLookup(options);
  const keys = Array.isArray(selectedKeys) ? selectedKeys : [];
  const normalized = keys
      .map((key) => {
        const tokens = tokenizeRejectValue(key);
        return tokens.map((token) => lookup.get(token)).find(Boolean) || String(key);
      });
  return unique(normalized).sort((a, b) => {
    const aIndex = options.findIndex((option) => option.key === a);
    const bIndex = options.findIndex((option) => option.key === b);
    if (aIndex !== bIndex) return aIndex - bIndex;
    return a.localeCompare(b);
  });
}

export function buildRejectSelectionSnapshot(params: {
  grain?: IGrain;
  profileId?: string;
  settingName?: string;
  selectedKeys?: string[];
  source?: 'profile' | 'job' | 'default';
}): IRejectSelectionSnapshot {
  const { grain } = params;
  const options = getRejectOptions(grain, { localizeLabels: false });
  const selectedKeys = normalizeRejectSelectionKeys(params.selectedKeys || [], options);
  const selectedOptionMap = new Map(options.map((option) => [option.key, option]));
  return {
    selectedKeys,
    selectedLabels: selectedKeys.map((key) => selectedOptionMap.get(key)?.label || key),
  };
}

export function normalizeRejectSelectionSessionConfigForReport(
  config: IRejectSelectionSessionConfig | undefined,
  grain: IGrain | undefined
): IRejectSelectionSessionConfig | undefined {
  if (!config) {
    return undefined;
  }

  const normalizeSnapshot = (snapshot: IRejectSelectionSnapshot | undefined): IRejectSelectionSnapshot => {
    const selectedValues = [
      ...(snapshot?.selectedKeys || []),
      ...(snapshot?.selectedLabels || []),
    ];
    return buildRejectSelectionSnapshot({
      grain,
      selectedKeys: selectedValues,
    });
  };

  return {
    particleDistribution: normalizeSnapshot(config.particleDistribution),
    rejectedGrains: normalizeSnapshot(config.rejectedGrains),
  };
}

export function buildRejectSelectionSessionConfig(params: {
  grain?: IGrain;
  profileId?: string;
  particleDistributionSelectedKeys: string[];
  rejectedGrainsSelectedKeys: string[];
  source?: 'profile' | 'job' | 'default';
}): IRejectSelectionSessionConfig {
  const {
    grain,
    particleDistributionSelectedKeys,
    rejectedGrainsSelectedKeys,
  } = params;

  return {
    particleDistribution: buildRejectSelectionSnapshot({
      grain,
      profileId: params.profileId,
      settingName: PARTICLE_DISTRIBUTION_REJECT_SETTING,
      selectedKeys: particleDistributionSelectedKeys,
    }),
    rejectedGrains: buildRejectSelectionSnapshot({
      grain,
      profileId: params.profileId,
      settingName: REJECTED_GRAINS_REJECT_SETTING,
      selectedKeys: rejectedGrainsSelectedKeys,
    }),
  };
}

export function resolveRejectSelectionSessionConfig(params: {
  grain?: IGrain;
  profileId?: string;
  storedValue?: unknown;
}): IRejectSelectionSessionConfig {
  const { grain, storedValue } = params;
  if (
    storedValue
    && typeof storedValue === 'object'
    && (storedValue as IRejectSelectionSessionConfig).particleDistribution
    && (storedValue as IRejectSelectionSessionConfig).rejectedGrains
  ) {
    const options = getRejectOptions(grain);
    const legacyValue = storedValue as {
      particleDistribution?: unknown;
      rejectedGrains?: unknown;
    };

    return {
      particleDistribution: buildRejectSelectionSnapshot({
        grain,
        selectedKeys: extractSelectedKeysFromSnapshot(legacyValue.particleDistribution, options),
      }),
      rejectedGrains: buildRejectSelectionSnapshot({
        grain,
        selectedKeys: extractSelectedKeysFromSnapshot(legacyValue.rejectedGrains, options),
      }),
    };
  }

  const defaults = getDefaultRejectSelectionConfig(grain);
  return buildRejectSelectionSessionConfig({
    grain,
    particleDistributionSelectedKeys: defaults.particleDistributionRejectKeys,
    rejectedGrainsSelectedKeys: defaults.rejectedGrainsRejectKeys,
  });
}

export function resolveRejectSelectionConfig(params: {
  grain?: IGrain;
  profile?: {
    particleDistributionRejectKeys?: string[];
    rejectedGrainsRejectKeys?: string[];
    rejectSelectionConfig?: {
      particleDistribution?: {
        selectedKeys?: string[];
      };
      rejectedGrains?: {
        selectedKeys?: string[];
      };
    };
  } | null;
}): IRejectSelectionConfig {
  const defaults = getDefaultRejectSelectionConfig(params.grain);
  const profile = params.profile || undefined;
  const legacyRejectSelectionConfig = profile?.rejectSelectionConfig;
  const particleDistribution = profile?.particleDistributionRejectKeys
    || legacyRejectSelectionConfig?.particleDistribution?.selectedKeys
    || defaults.particleDistributionRejectKeys;
  const rejectedGrains = profile?.rejectedGrainsRejectKeys
    || legacyRejectSelectionConfig?.rejectedGrains?.selectedKeys
    || defaults.rejectedGrainsRejectKeys;

  const options = getRejectOptions(params.grain);
  return {
    particleDistributionRejectKeys: normalizeRejectSelectionKeys(particleDistribution, options),
    rejectedGrainsRejectKeys: normalizeRejectSelectionKeys(rejectedGrains, options),
  };
}

export function isRejectSelectionApplicable(grain: IGrain | undefined): boolean {
  return getRejectOptions(grain).length > 0;
}

export function filterRowsByRejectSelection(
  rows: string[][],
  selectedKeys: string[] | undefined,
  grain: IGrain | undefined
): string[][] {
  const options = getRejectOptions(grain);
  const normalizedSelection = normalizeRejectSelectionKeys(selectedKeys || [], options);
  const aliasLookup = new Map<string, string[]>();
  options.forEach((option) => {
    aliasLookup.set(option.key, unique([option.key, option.label, ...option.aliases].map(normalizeToken)));
  });

  const knownRejectTokens = new Set(
    options.flatMap((option) => unique([option.key, option.label, ...option.aliases].map(normalizeToken)))
  );

  const hasRejectAnnotations = rows.some((row) =>
    row.some((cell) => knownRejectTokens.has(normalizeToken(cell)))
  );

  if (normalizedSelection.length === 0) {
    return hasRejectAnnotations ? [] : rows;
  }

  if (!hasRejectAnnotations) {
    return rows;
  }

  const selectedTokens = new Set(
    normalizedSelection.flatMap((key) => aliasLookup.get(key) || [normalizeToken(key)])
  );

  return rows.filter((row) =>
    row.some((cell) => selectedTokens.has(normalizeToken(cell)))
  );
}

export function filterRowsByRejectSnapshot(
  rows: string[][],
  snapshot:
    | {
        selectedKeys?: string[];
        selectedLabels?: string[];
      }
    | undefined,
  grain: IGrain | undefined
): string[][] {
  if (!snapshot) {
    return rows;
  }

  const rawSelectedTokens = unique([
    ...(snapshot.selectedKeys || []),
    ...(snapshot.selectedLabels || [])
  ].flatMap(tokenizeRejectValue));
  const options = grain ? getRejectOptions(grain) : [];
  const normalizedSelectedKeys = grain
    ? normalizeRejectSelectionKeys([...(snapshot.selectedKeys || []), ...(snapshot.selectedLabels || [])], options)
    : [];
  const optionByKey = new Map(options.map((option) => [option.key, option]));
  const selectedTokens = new Set([
    ...rawSelectedTokens,
    ...normalizedSelectedKeys.flatMap((key) => {
      const option = optionByKey.get(key);
      return option
        ? unique([option.key, option.label, ...option.aliases].flatMap(tokenizeRejectValue))
        : tokenizeRejectValue(key);
    })
  ]);

  const aliasTokens = grain
    ? new Set(
        options.flatMap((option) => unique([option.key, option.label, ...option.aliases].flatMap(tokenizeRejectValue)))
      )
    : selectedTokens;

  if (selectedTokens.size === 0) {
    const hasRejectAnnotations = rows.some((row) =>
      row.some((cell) => aliasTokens.has(normalizeToken(cell)))
    );
    return hasRejectAnnotations ? [] : rows;
  }

  const hasRejectAnnotations = rows.some((row) =>
    row.some((cell) => aliasTokens.has(normalizeToken(cell)))
  );
  if (!hasRejectAnnotations) {
    return rows;
  }

  return rows.filter((row) =>
    row.some((cell) => tokenizeRejectValue(cell).some((token) => selectedTokens.has(token)))
  );
}

export function filterMeasurementRowsByRejectSnapshot(
  header: string[] | undefined,
  rows: string[][],
  snapshot:
    | {
        selectedKeys?: string[];
        selectedLabels?: string[];
      }
    | undefined
): string[][] {
  return filterMeasurementRowsByRejectSelection(header, rows, snapshot);
}

export function calculateRejectedWeightSummary(
  header: string[] | undefined,
  rows: string[][],
  snapshot:
    | {
        selectedKeys?: string[];
        selectedLabels?: string[];
      }
    | undefined,
  grain?: IGrain
): { rejectionPercent: number; goodPercent: number; filtered: boolean } | null {
  if (!Array.isArray(header) || header.length === 0 || !Array.isArray(rows) || rows.length === 0) {
    return null;
  }

  const percentByWeightIndex = header.findIndex((column) => normalizeToken(column) === 'percentbyweight');
  if (percentByWeightIndex === -1) {
    return null;
  }

  const typeIndex = header.findIndex((column) => {
    const token = normalizeToken(column);
    return token === 'type' || token === 'categoryid';
  });

  const dataRows = rows.filter((row) => {
    if (typeIndex === -1) {
      return true;
    }
    const token = normalizeToken(row?.[typeIndex]);
    return token !== 'total' && token !== 'grandtotal';
  });

  const filteredRows = snapshot
    ? filterRowsByRejectSnapshot(dataRows, snapshot, grain)
    : dataRows;
  const rejectionPercent = filteredRows.reduce((sum, row) => {
    const value = Number(row?.[percentByWeightIndex]);
    return Number.isFinite(value) ? sum + value : sum;
  }, 0);

  return {
    rejectionPercent,
    goodPercent: Math.max(0, 100 - rejectionPercent),
    filtered: Boolean(snapshot) && filteredRows.length !== dataRows.length,
  };
}
